import { Box, Modal } from '@mui/material'
import { useCallback, useEffect } from 'react'
import ReactPlayer from 'react-player'
import { IconButton } from 'utoniq-ui'

import { VideoPlayerControl } from '@/components/organisms/resourcePlayer/VideoPlayer/VideoPlayerControl'
import { styles } from '@/components/styles'
import { useSoundPlayer } from '@/core'

type Props = {
  uri: string
  autoplay?: boolean
  open: boolean
  onClose: () => void
}

export const VideoPurePlayer = ({ uri, autoplay, open, onClose }: Props) => (
  <Modal open={open} onClose={() => onClose()}>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        zIndex: 10,
        left: 0,
        right: 0,
        bottom: 0,
        outline: 'none',
        width: '100%',
        height: '100%',
        bgcolor: 'black',
        'video, iframe': {
          display: 'block',
        },
      }}
    >
      <VideoPurePlayerContent uri={uri} autoplay={autoplay} onClose={onClose} />
    </Box>
  </Modal>
)

const VideoPurePlayerContent = ({
  uri,
  autoplay,
  onClose,
}: {
  uri: string
  autoplay?: boolean
  onClose: () => void
}) => {
  const {
    player,
    playing,
    duration,
    sliderValue,
    handleDuration,
    handleProgress,
    change,
    commitChange,
    rewind,
    end,
    play,
    pause,
    forward,
    handleError,
  } = useSoundPlayer(uri)

  useEffect(() => {
    if (!player) return

    autoplay ? play() : pause()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, autoplay])

  const handleClose = useCallback(() => {
    pause()
    onClose()
  }, [onClose, pause])

  return (
    <>
      <ReactPlayer
        ref={player}
        width="100%"
        height="100%"
        playsinline
        playIcon={<Box />}
        url={uri}
        playing={playing}
        onDuration={handleDuration}
        onProgress={handleProgress}
        onEnded={end}
        onError={handleError}
        progressInterval={100}
        config={{ file: { attributes: { poster: 'noposter' } } }}
      />
      {player && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              pt: styles.safeAreaTop,
            }}
          >
            <IconButton.Close
              size="medium"
              bgcolor="grey.50"
              onClick={handleClose}
            />
          </Box>
          <VideoPlayerControl
            isPlaying={playing}
            duration={duration ?? 0}
            onChange={change}
            onChangeCommitted={commitChange}
            sliderValue={sliderValue ?? 0}
            onPlay={play}
            onPause={pause}
            onRewind={rewind}
            onForward={forward}
          />
        </>
      )}
    </>
  )
}
